import { useEffect, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Textarea } from '../../components/atoms/Textarea'
import { Field } from '../../components/molecules/Field'
import type {
  CreateSupplierInput,
  SupplierDto,
  UpdateSupplierInput
} from '../../../viewmodels/useSuppliers'
import type { SupplierType } from '../../../models/supplier.model'

const NAME_MAX = 128
const DESC_MAX = 1024
const CONTACT_MAX = 1024

interface Props {
  readonly open: boolean
  readonly mode: 'create' | 'edit'
  readonly initial: SupplierDto | null
  readonly onClose: () => void
  readonly onSubmit: (
    input: CreateSupplierInput | UpdateSupplierInput
  ) => Promise<{ ok: true } | { ok: false; field?: string | null; message: string }>
}

interface FormState {
  name: string
  description: string
  contact_info: string
  supplier_type: SupplierType
}

// Two-value segmented control. 'raw_material' is the default for new
// suppliers since that's been the only category until migration 0077.
const SUPPLIER_TYPE_OPTIONS: ReadonlyArray<{ value: SupplierType; label: string; hint: string }> = [
  { value: 'raw_material', label: 'Raw material', hint: 'Vendor of bamboo, fittings, consumables' },
  { value: 'artisan',      label: 'Artisan',      hint: 'Handcrafts finished product for us' }
]

const EMPTY: FormState = { name: '', description: '', contact_info: '', supplier_type: 'raw_material' }

function clientValidate(state: FormState): { field: keyof FormState; message: string } | null {
  const name = state.name.trim()
  if (name.length < 1 || name.length > NAME_MAX) {
    return { field: 'name', message: `name must be 1..${NAME_MAX} chars` }
  }
  if (state.description.length > DESC_MAX) {
    return { field: 'description', message: `description must be ≤${DESC_MAX} chars` }
  }
  if (state.contact_info.length > CONTACT_MAX) {
    return { field: 'contact_info', message: `contact_info must be ≤${CONTACT_MAX} chars` }
  }
  return null
}

/**
 * SupplierFormModal — Add + Edit shared form. Captures the supplier's
 * Name, Description, and free-form Contact info.
 */
export function SupplierFormModal({
  open,
  mode,
  initial,
  onClose,
  onSubmit
}: Props): JSX.Element {
  const [state, setState] = useState<FormState>(EMPTY)
  const [submitting, setSubmitting] = useState(false)
  const [fieldError, setFieldError] = useState<{ field: keyof FormState; message: string } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setState(
        initial === null
          ? EMPTY
          : {
              name: initial.name,
              description: initial.description ?? '',
              contact_info: initial.contact_info ?? '',
              supplier_type: initial.supplier_type
            }
      )
      setFieldError(null)
      setFormError(null)
      setSubmitting(false)
    }
  }, [open, initial])

  const handleSubmit = async (): Promise<void> => {
    const clientErr = clientValidate(state)
    if (clientErr !== null) {
      setFieldError({ field: clientErr.field, message: clientErr.message })
      return
    }
    setFieldError(null)
    setFormError(null)
    setSubmitting(true)
    const payload: CreateSupplierInput | UpdateSupplierInput = {
      name: state.name.trim(),
      description: state.description === '' ? null : state.description,
      contact_info: state.contact_info === '' ? null : state.contact_info,
      supplier_type: state.supplier_type
    }
    const result = await onSubmit(payload)
    setSubmitting(false)
    if (result.ok) {
      onClose()
      return
    }
    const f = result.field
    if (f !== null && f !== undefined && f in EMPTY) {
      setFieldError({ field: f as keyof FormState, message: result.message })
    } else {
      setFormError(result.message)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'Add supplier' : `Edit ${initial?.name ?? ''}`}
      maxWidth="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError !== null ? <Alert tone="error">{formError}</Alert> : null}

        <Field label="Name" error={fieldError?.field === 'name' ? fieldError.message : null}>
          <Input
            value={state.name}
            hasError={fieldError?.field === 'name'}
            onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
            placeholder="e.g. Karnataka Bamboo Co."
            maxLength={NAME_MAX}
          />
        </Field>

        <Field
          label="Type"
          helper="What this supplier provides."
          error={null}
        >
          <div role="radiogroup" aria-label="Supplier type" className="flex flex-wrap gap-2">
            {SUPPLIER_TYPE_OPTIONS.map((opt) => {
              const selected = state.supplier_type === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setState((s) => ({ ...s, supplier_type: opt.value }))}
                  className={[
                    'rounded-full border px-4 py-2 font-body text-body-sm transition',
                    selected
                      ? 'border-forest bg-forest text-text-light shadow-sm'
                      : 'border-border bg-bg-primary text-text-secondary hover:border-forest/60 hover:text-charcoal'
                  ].join(' ')}
                  title={opt.hint}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
        </Field>

        <Field label="Description" error={fieldError?.field === 'description' ? fieldError.message : null}>
          <Textarea
            value={state.description}
            rows={3}
            hasError={fieldError?.field === 'description'}
            onChange={(e) => setState((s) => ({ ...s, description: e.target.value }))}
            placeholder="Optional"
            maxLength={DESC_MAX}
          />
        </Field>

        <Field label="Contact info" error={fieldError?.field === 'contact_info' ? fieldError.message : null}>
          <Textarea
            value={state.contact_info}
            rows={3}
            hasError={fieldError?.field === 'contact_info'}
            onChange={(e) => setState((s) => ({ ...s, contact_info: e.target.value }))}
            placeholder="Phone, email, address — free-form"
            maxLength={CONTACT_MAX}
          />
        </Field>
      </div>
    </Modal>
  )
}
